import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LadderContext = createContext();

export const LadderProvider = ({ children }) => {
  const { getAuthHeaders } = useAuth();
  const [ladder, setLadder] = useState(null);
  const [players, setPlayers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLadder = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/ladder/active`);
      setLadder(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch ladder:', error);
      return null;
    }
  }, []);

  const fetchPlayers = useCallback(async (ladderId) => {
    if (!ladderId) return;
    try {
      const response = await axios.get(`${API}/ladder/${ladderId}/players`);
      setPlayers(response.data);
    } catch (error) {
      console.error('Failed to fetch players:', error);
    }
  }, []);

  const fetchMatches = useCallback(async (ladderId) => {
    if (!ladderId) return;
    try {
      const response = await axios.get(`${API}/ladder/${ladderId}/matches`);
      setMatches(response.data);
    } catch (error) {
      console.error('Failed to fetch matches:', error);
    }
  }, []);

  const fetchLeaderboard = useCallback(async (ladderId) => {
    if (!ladderId) return;
    try {
      const response = await axios.get(`${API}/ladder/${ladderId}/leaderboard`);
      setLeaderboard(response.data);
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    }
  }, []);

  const fetchPartners = useCallback(async (ladderId) => {
    if (!ladderId) return;
    try {
      const response = await axios.get(`${API}/ladder/${ladderId}/partners`);
      setPartners(response.data);
    } catch (error) {
      console.error('Failed to fetch partners:', error);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    const ladderData = await fetchLadder();
    if (ladderData?.id) {
      await Promise.all([
        fetchPlayers(ladderData.id),
        fetchMatches(ladderData.id),
        fetchLeaderboard(ladderData.id),
        fetchPartners(ladderData.id)
      ]);
    }
    setLoading(false);
  }, [fetchLadder, fetchPlayers, fetchMatches, fetchLeaderboard, fetchPartners]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  // Admin actions
  const createLadder = useCallback(async (name) => {
    const response = await axios.post(`${API}/ladder?name=${encodeURIComponent(name)}`, {}, {
      headers: getAuthHeaders()
    });
    setLadder(response.data);
    return response.data;
  }, [getAuthHeaders]);

  const updateLadder = useCallback(async (ladderId, data) => {
    const response = await axios.put(`${API}/ladder/${ladderId}`, data, {
      headers: getAuthHeaders()
    });
    setLadder(response.data);
    return response.data;
  }, [getAuthHeaders]);

  const resetLadder = useCallback(async (ladderId) => {
    const response = await axios.post(`${API}/ladder/${ladderId}/reset`, {}, {
      headers: getAuthHeaders()
    });
    setLadder(response.data);
    await refreshAll();
    return response.data;
  }, [getAuthHeaders, refreshAll]);

  const uploadLogo = useCallback(async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API}/upload/logo`, formData, {
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data.url;
  }, [getAuthHeaders]);

  const addPlayer = useCallback(async (ladderId, playerData) => {
    const response = await axios.post(`${API}/ladder/${ladderId}/players`, playerData, {
      headers: getAuthHeaders()
    });
    await fetchPlayers(ladderId);
    await fetchLeaderboard(ladderId);
    return response.data;
  }, [getAuthHeaders, fetchPlayers, fetchLeaderboard]);

  const updatePlayer = useCallback(async (playerId, data) => {
    const response = await axios.put(`${API}/players/${playerId}`, data, {
      headers: getAuthHeaders()
    });
    if (ladder?.id) {
      await fetchPlayers(ladder.id);
      await fetchLeaderboard(ladder.id);
    }
    return response.data;
  }, [getAuthHeaders, ladder?.id, fetchPlayers, fetchLeaderboard]);

  const deletePlayer = useCallback(async (playerId) => {
    await axios.delete(`${API}/players/${playerId}`, {
      headers: getAuthHeaders()
    });
    if (ladder?.id) {
      await fetchPlayers(ladder.id);
      await fetchLeaderboard(ladder.id);
    }
  }, [getAuthHeaders, ladder?.id, fetchPlayers, fetchLeaderboard]);

  const refreshPlayers = useCallback(async () => {
    if (ladder?.id) {
      await fetchPlayers(ladder.id);
    }
  }, [ladder?.id, fetchPlayers]);

  const addMatch = useCallback(async (ladderId, matchData) => {
    const response = await axios.post(`${API}/ladder/${ladderId}/matches`, matchData, {
      headers: getAuthHeaders()
    });
    await Promise.all([
      fetchMatches(ladderId),
      fetchLeaderboard(ladderId),
      fetchPartners(ladderId)
    ]);
    return response.data;
  }, [getAuthHeaders, fetchMatches, fetchLeaderboard, fetchPartners]);

  const updateMatch = useCallback(async (matchId, data) => {
    const response = await axios.put(`${API}/matches/${matchId}`, data, {
      headers: getAuthHeaders()
    });
    if (ladder?.id) {
      await Promise.all([
        fetchMatches(ladder.id),
        fetchLeaderboard(ladder.id),
        fetchPartners(ladder.id)
      ]);
    }
    return response.data;
  }, [getAuthHeaders, ladder?.id, fetchMatches, fetchLeaderboard, fetchPartners]);

  const deleteMatch = useCallback(async (matchId) => {
    await axios.delete(`${API}/matches/${matchId}`, {
      headers: getAuthHeaders()
    });
    if (ladder?.id) {
      await Promise.all([
        fetchMatches(ladder.id),
        fetchLeaderboard(ladder.id),
        fetchPartners(ladder.id)
      ]);
    }
  }, [getAuthHeaders, ladder?.id, fetchMatches, fetchLeaderboard, fetchPartners]);

  const getPlayerById = useCallback((playerId) => {
    return players.find(p => p.id === playerId);
  }, [players]);

  const value = useMemo(() => ({
    ladder,
    players,
    matches,
    leaderboard,
    partners,
    loading,
    refreshAll,
    refreshPlayers,
    createLadder,
    updateLadder,
    resetLadder,
    uploadLogo,
    addPlayer,
    updatePlayer,
    deletePlayer,
    addMatch,
    updateMatch,
    deleteMatch,
    getPlayerById
  }), [
    ladder, players, matches, leaderboard, partners, loading,
    refreshAll, refreshPlayers, createLadder, updateLadder, resetLadder, uploadLogo,
    addPlayer, updatePlayer, deletePlayer, addMatch, updateMatch, deleteMatch, getPlayerById
  ]);

  return (
    <LadderContext.Provider value={value}>
      {children}
    </LadderContext.Provider>
  );
};

export const useLadder = () => {
  const context = useContext(LadderContext);
  if (!context) {
    throw new Error('useLadder must be used within a LadderProvider');
  }
  return context;
};
